import { Variants } from "framer-motion";

/**
 * XperfoMax Motion System
 * 
 * Calm, premium, strategy-led Framer Motion animations.
 * Motion is functional, not expressive.
 * 
 * PRINCIPLES:
 * - No bounce, spring, elastic, or playful easing
 * - Animations trigger once on scroll
 * - Consistent values across the entire site
 * - Reduced motion respected
 */

// ============================================
// GLOBAL EASING - One curve for everything
// ============================================
export const ease = [0.22, 1, 0.36, 1] as const;

// ============================================
// VIEWPORT OPTIONS - Consistent trigger points
// ============================================
export const viewportOnce = { once: true, margin: "-80px" };
export const viewportOnceNear = { once: true, margin: "-50px" };

// ============================================
// SECTION CONTAINER VARIANT
// Standard sections: fade + slight rise
// ============================================
export const sectionVariant: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: ease,
    },
  },
};

// ============================================
// HERO VARIANT
// Larger distance for hero content
// ============================================
export const heroVariant: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: ease,
    },
  },
};

// ============================================
// STAGGER CONTAINER VARIANT
// Parent container that staggers children
// ============================================
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.08,
    },
  },
};

// ============================================
// ITEM VARIANT
// Cards, steps, bullets - smaller movement
// ============================================
export const itemVariant: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: ease,
    },
  },
};

// ============================================
// FADE ONLY VARIANT
// Subtle fade without movement
// ============================================
export const fadeVariant: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: ease,
    },
  },
};

// ============================================
// SLIDE IN LEFT VARIANT
// For bullet points and statements
// ============================================
export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: ease,
    },
  },
};

// ============================================
// DIAGRAM VARIANT
// System overview diagrams - subtle scale (0.97 per spec)
// ============================================
export const diagramVariant: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: ease,
    },
  },
};

// ============================================
// SCALE LINE VARIANT
// For connection lines in diagrams
// ============================================
export const scaleLineVariant: Variants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: {
      duration: 0.8,
      delay: 0.3,
      ease: ease,
    },
  },
};

// ============================================
// HOVER TRANSITIONS
// Approved hover behaviors
// ============================================
export const cardHover = {
  y: -2,
  transition: { type: "tween" as const, duration: 0.2, ease: [...ease] as [number, number, number, number] },
};

export const subtleHover = {
  y: -2,
  boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
};

// ============================================
// GLOW ANIMATION VARIANT
// Background ambient glows (very subtle) - uses approved easing
// ============================================
export const glowVariant: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: ease,
    },
  },
};

// ============================================
// DELAYED VARIANTS
// For sequential reveal within sections
// ============================================
export const createDelayedVariant = (delay: number): Variants => ({
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      delay,
      ease: ease,
    },
  },
});

// ============================================
// ORBIT LABELS (Hero visual)
// Subtle position-based reveals
// ============================================
export const orbitLabelUp: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.6, duration: 0.5, ease: ease },
  },
};

export const orbitLabelRight: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { delay: 0.7, duration: 0.5, ease: ease },
  },
};

export const orbitLabelDown: Variants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.8, duration: 0.5, ease: ease },
  },
};

export const orbitLabelLeft: Variants = {
  hidden: { opacity: 0, x: 10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { delay: 0.9, duration: 0.5, ease: ease },
  },
};

// ============================================
// SYSTEM EMERGENCE ANIMATION
// Growth System Diagram - one-time entrance
// ============================================

// Container: fade + micro-scale
export const systemDiagramContainer: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: ease,
    },
  },
};

// Center node: fade in after container
export const systemCenterNode: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.3,
      duration: 0.5,
      ease: ease,
    },
  },
};

// Labels stagger container
export const systemLabelsContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.5,
      staggerChildren: 0.12,
    },
  },
};

// Individual label variant
export const systemLabelItem: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: ease,
    },
  },
};

// ============================================
// PROBLEM SECTION ANIMATION SYSTEM
// Enterprise-grade, diagnostic animation pattern
// Calm, authoritative, no theatrics
// ============================================

// Problem section container: gentle fade + slight rise
export const problemSectionVariant: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Problem section stagger container for children
export const problemStaggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
    },
  },
};

// Mobile-optimized problem stagger (reduced delays)
export const problemStaggerContainerMobile: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

// Vertical accent line animation: grows top to bottom
// Duration: 0.8s, Delay: 0.15s as per spec
export const problemAccentLineVariant: Variants = {
  hidden: { scaleY: 0, originY: 0 },
  visible: {
    scaleY: 1,
    transition: {
      duration: 0.8,
      delay: 0.15,
      ease: "easeOut",
    },
  },
};

// Problem bullet reveal: subtle slide from left
// x: -6px → 0, duration: 0.35s as per spec
export const problemBulletVariant: Variants = {
  hidden: { opacity: 0, x: -6 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.35,
      ease: "easeOut",
    },
  },
};

// Mobile problem bullet: fade only, no horizontal movement
// Stagger: 0.08s, hover effects disabled
export const problemBulletVariantMobile: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.35,
      ease: "easeOut",
    },
  },
};

// Problem section header (label + title)
export const problemHeaderVariant: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};
